/**
 * Mountain Brook's pink Building Permit Application, filled from the job
 * (ADR 0289).
 *
 * Jonathon, 2026-10-15: "we just need the pink form filled out whenever the
 * city is mountainbrook and we click on the permit button. then that goes into
 * docs once it's filled out. then we need to be able to click on the jot form
 * link". The white certification is signed on the city's JotForm, so
 * nothing here is signed by the customer: the office checks the pink sheet,
 * saves it to Docs as a PDF, and goes on to the city's JotForm.
 *
 * Everything the sheet says and every rule about what goes on it is here, so
 * the screen the office edits it on and the PDF saved to Docs read one object
 * (CLAUDE.md rule 5). Only the boxes Jonathon filled by hand on Rena Lucas's
 * paper form are filled; the rest is the city's.
 */

import { isNewConstruction } from "./autoExpenses.js";
import { COMPANY_NAME } from "./constants.js";
import { COMPANY_SIGNER_NAME } from "./contract.js";
import { cents, formatCents, toCents, type Cents } from "./money.js";
import { centralDate } from "./payoutReminder.js";
import { formatPhone, primaryPhone } from "./phone.js";

// ------------------------------------------------------------ the city ---

/**
 * The key Mountain Brook's licence number is kept under in
 * `company_settings.permit_city_license_numbers`.
 */
export const MOUNTAIN_BROOK = "mountain_brook";

/**
 * The city's Construction Value and Scope of Work Certification, signed on
 * JotForm. Opened from the Permit prompt once the pink form is saved.
 * Jonathon pasted it on 2026-10-15.
 */
export const MOUNTAIN_BROOK_PERMIT_FORM_URL: string | null =
  "https://www.jotform.com/sign/262456833215054/invite/01m1mgck9p4bccefa69f0bd04f";

/** Letters only, lower case: "Mtn. Brook" and "MOUNTAIN BROOK" read alike. */
const squash = (value: unknown): string =>
  typeof value === "string" ? value.toLowerCase().replace(/[^a-z]/g, "") : "";

/**
 * Whether the job is in Mountain Brook, by the city on its address.
 *
 * The job's own address first, and the contact's only where the job has none,
 * the same order the form fills the property from (ADR 0003). "Mtn Brook" is
 * how it gets typed in a hurry, so it counts too.
 */
export function isMountainBrook(job: {
  address?: string | null;
  city?: string | null;
  contact?: { city?: string | null } | null;
}): boolean {
  const city =
    text(job.address) !== "" || text(job.city) !== ""
      ? job.city
      : job.contact?.city;
  const name = squash(city);
  return name === "mountainbrook" || name === "mtnbrook";
}

/**
 * The first day an estimate is charged Mountain Brook's own permit fee.
 * Jonathon, 2026-10-16, asked whether estimates already priced should change:
 * "New estimates only". So one made before today keeps 1%, $150 at least.
 */
export const MOUNTAIN_BROOK_FEE_FROM = "2026-10-16";

/**
 * Whether an estimate's permit is Mountain Brook's fee.
 *
 * The job's city is read live, so an address put right later moves the
 * permit with it. The estimate's own date decides old from new, read as the
 * Alabama day it was made. An estimate with no date yet is being made now.
 */
export function mountainBrookFeeApplies(
  place:
    | {
        address?: string | null;
        city?: string | null;
        contact?: { city?: string | null } | null;
      }
    | null
    | undefined,
  estimateCreatedAt: string | null | undefined,
): boolean {
  if (!place || !isMountainBrook(place)) return false;
  if (!estimateCreatedAt) return true;
  const made = new Date(estimateCreatedAt);
  if (Number.isNaN(made.getTime())) return true;
  return centralDate(made) >= MOUNTAIN_BROOK_FEE_FROM;
}

/** What the "Other" line under "Repair" says until the office changes it. */
export const PERMIT_DEFAULT_WORK_TYPE = "Roof Replacement";

/**
 * Who signs for the company when no contact is set in Settings: the
 * contract's signer, who is Lysa. Jonathon, 2026-10-07: "lysa is the
 * contact. use her name, email, and number".
 */
export const PERMIT_SIGNER_NAME = COMPANY_SIGNER_NAME;

// ------------------------------------------------------------- the form ---

export interface PermitParty {
  name: string;
  /** The street line. */
  address: string;
  city: string;
  state: string;
  phone: string;
}

/** Everything filled on the pink sheet, as the office left it. */
export interface PermitApplication {
  /** yyyy-mm-dd: the date beside the company's signature. */
  preparedOn: string;
  /** "Job Location". */
  site: { street: string; city: string; state: string; zip: string };
  owner: PermitParty;
  contractor: PermitParty & {
    /** The state licence, "State Lic#". */
    licenseNumber: string;
    /** The company's licence with the city, "Mtn Brook Lic #". */
    cityLicenseNumber: string;
    /** Signed in script and printed beneath. */
    signerName: string;
  };
  /**
   * "New Construction" is ticked rather than "Repair" under "Type Improvement
   * Permit is for". Jonathon, 2026-10-20, asked whether a new build should
   * tick it: "yes". Left out, "Repair" is ticked.
   */
  newConstruction?: boolean;
  /** Written on the line under the ticked box. */
  workType: string;
  /** yyyy-mm-dd, or "" when the office left it blank. */
  proposedStart: string;
  proposedEnd: string;
  /** "Total Contract/Construction Amount". Integer cents. */
  contractAmount: Cents;
}

export interface BuildPermitApplicationInput {
  /** yyyy-mm-dd, from the office's own device. */
  today: string;
  job: {
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  };
  contact: {
    full_name?: string | null;
    cell?: string | null;
    phone?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  } | null;
  company: {
    name?: string | null;
    address?: string | null;
    license_number?: string | null;
    /** The company's licence with each city, by city key. */
    permit_city_license_numbers?: Record<string, unknown> | null;
  } | null;
  /** The permit contact's profile: Lysa. */
  signer: { full_name?: string | null; phone?: string | null } | null;
  proposedStart: string | null;
  proposedEnd: string | null;
  contractAmount: Cents;
  /** The trades of the job's active estimates, to tell a new build. */
  trades?: readonly (string | null | undefined)[];
}

function text(value: unknown): string {
  if (typeof value === "string") return value.trim();
  return value == null ? "" : String(value).trim();
}

/**
 * "626 Valleyview Dr, Pelham, AL 35124" as the boxes the form has.
 *
 * The company's address is one line in Settings and the form wants the street,
 * the city and the state apart. The last two commas decide it; an address that
 * does not have them goes in whole as the street, so nothing is lost.
 */
export function splitAddress(address: string | null | undefined): {
  street: string;
  city: string;
  state: string;
  zip: string;
} {
  const parts = text(address)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length < 3) {
    return { street: parts.join(", "), city: "", state: "", zip: "" };
  }
  const [city = "", last = ""] = parts.slice(-2);
  const [state = "", zip = ""] = last.split(/\s+/);
  return { street: parts.slice(0, -2).join(", "), city, state, zip };
}

/** The property, from the job, or from the contact where the job has none. */
function siteOf(input: BuildPermitApplicationInput): PermitApplication["site"] {
  const fromJob = text(input.job.address) !== "";
  const place = fromJob ? input.job : (input.contact ?? {});
  return {
    street: text(place.address),
    city: text(place.city),
    state: text(place.state) || (fromJob ? "AL" : ""),
    zip: text(place.zip),
  };
}

/** The company, at its own address, with Lysa's number. */
function contractorOf(
  input: BuildPermitApplicationInput,
): PermitApplication["contractor"] {
  const company = input.company ?? {};
  const signer = input.signer ?? {};
  const at = splitAddress(company.address);
  return {
    // CLAUDE.md rule 11: the name is data; the constant is only the fallback.
    name: text(company.name) || COMPANY_NAME,
    address: at.street,
    city: at.city,
    state: at.state,
    phone: formatPhone(signer.phone) ?? "",
    licenseNumber: text(company.license_number),
    cityLicenseNumber: text(
      company.permit_city_license_numbers?.[MOUNTAIN_BROOK] ?? "",
    ),
    signerName: text(signer.full_name) || PERMIT_SIGNER_NAME,
  };
}

/**
 * The pink sheet, filled from the job.
 *
 * The property is the job's address and the owner lives there. The contractor
 * is the company's row and Lysa's profile. Every figure and date arrives
 * already decided; this only lays them out.
 */
export function buildPermitApplication(
  input: BuildPermitApplicationInput,
): PermitApplication {
  const site = siteOf(input);
  return {
    preparedOn: input.today,
    site,
    owner: {
      name: text(input.contact?.full_name),
      address: site.street,
      city: site.city,
      state: site.state,
      phone: formatPhone(primaryPhone(input.contact)) ?? "",
    },
    contractor: contractorOf(input),
    newConstruction: (input.trades ?? []).some(isNewConstruction),
    workType: PERMIT_DEFAULT_WORK_TYPE,
    proposedStart: text(input.proposedStart),
    proposedEnd: text(input.proposedEnd),
    contractAmount: input.contractAmount,
  };
}

// ------------------------------------------------------------ defaults ---

/**
 * When the work is booked to start and finish.
 *
 * The earliest start and the latest finish across the job's work orders: a
 * roof and its gutters on two days is one permit covering both. An order with
 * no finish counts its start as its finish. With no booking at all, the job's
 * work date for both, and with no work date, nothing: the office types them.
 *
 * `work_date` is a `date` column and is sliced, never handed to `new Date`,
 * which reads it as midnight UTC and gives the day before. The orders'
 * timestamps are read as the Central-time day they fall on.
 */
export function permitDateDefaults(
  job: { work_date?: string | null },
  orders: readonly { start_at?: string | null; end_at?: string | null }[],
): { start: string; end: string } {
  const starts: string[] = [];
  const ends: string[] = [];
  for (const order of orders) {
    const start = order.start_at ? centralDate(new Date(order.start_at)) : null;
    const end = order.end_at ? centralDate(new Date(order.end_at)) : start;
    if (start) starts.push(start);
    if (end) ends.push(end);
  }
  starts.sort();
  ends.sort();
  const workDate = text(job.work_date).slice(0, 10);
  return {
    start: starts[0] ?? workDate,
    end: ends[ends.length - 1] ?? workDate,
  };
}

/**
 * The amount as the form's box reads it: "24,533.32", no dollar sign, because
 * the form prints its own "$" in front of the line.
 */
export function permitAmountText(amount: Cents): string {
  return formatCents(cents(Math.max(0, Number(amount) || 0)), "en-US");
}

/** "24,533.32" or "$24,533.32" typed into the box, back to cents. */
export function parsePermitAmount(typed: string): Cents | null {
  const cleaned = typed.replace(/[$,\s]/g, "");
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  return toCents(cleaned);
}

/** "10/12/26": how the form's date boxes read, the way they were handwritten. */
export function permitDateText(date: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(text(date));
  if (!match) return "";
  const [, year = "", month, day] = match;
  return `${Number(month)}/${Number(day)}/${year.slice(2)}`;
}

/**
 * What the saved PDF is called in Docs: the city, the paper, and whose house,
 * so it is the one picked out of the file chooser on the city's form.
 */
export function permitFileName(form: PermitApplication): string {
  const owner = text(form.owner.name).replace(/[\\/:*?"<>|]/g, "");
  return owner
    ? `Mountain Brook permit application - ${owner}.pdf`
    : "Mountain Brook permit application.pdf";
}
